import { z } from 'zod';

import { fixture } from './fixtures';

export const TeamRequestChangeSchema = z.object({
	id: z.string(),
	changeType: z.enum(['add_report', 'remove_report', 'change_manager']),
	state: z.enum(['pending_confirmation', 'pending_admin', 'approved', 'declined']),
	targetUserId: z.string().nullable(),
	currentManagerId: z.string().nullable(),
	newManagerId: z.string().nullable(),
	confirmedById: z.string().nullable(),
	confirmedAt: z.string().nullable(),
	decidedById: z.string().nullable(),
	decidedAt: z.string().nullable(),
	targetUser: z
		.object({
			id: z.string(),
			displayName: z.string(),
			profilePicUrl: z.string().optional(),
			username: z.string().optional(),
		})
		.nullable()
		.optional(),
	currentManager: z
		.object({
			id: z.string(),
			displayName: z.string(),
			profilePicUrl: z.string().optional(),
			username: z.string().optional(),
		})
		.nullable()
		.optional(),
	newManager: z
		.object({
			id: z.string(),
			displayName: z.string(),
			profilePicUrl: z.string().optional(),
			username: z.string().optional(),
		})
		.nullable()
		.optional(),
});

export type TeamRequestChange = z.infer<typeof TeamRequestChangeSchema>;

export const TeamRequestSchema = z.object({
	id: z.string(),
	companyId: z.string(),
	requesterId: z.string(),
	state: z.enum(['pending', 'approved', 'declined', 'cancelled']),
	submittedAt: z.string(),
	decidedAt: z.string().nullable(),
	decidedById: z.string().nullable(),
	adminNotes: z.string().nullable(),
	requester: z
		.object({
			id: z.string(),
			displayName: z.string(),
			profilePicUrl: z.string().optional(),
			username: z.string().optional(),
		})
		.nullable()
		.optional(),
	decidedBy: z
		.object({
			id: z.string(),
			displayName: z.string(),
		})
		.nullable()
		.optional(),
	changes: z.array(TeamRequestChangeSchema),
});

export type TeamRequest = z.infer<typeof TeamRequestSchema>;

export const TeamRequestListSchema = z.object({
	records: z.array(TeamRequestSchema),
	totalCount: z.number().optional(),
	page: z.number().optional(),
	perPage: z.number().optional(),
});

export type TeamRequestList = z.infer<typeof TeamRequestListSchema>;

const EmbeddedUserSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	profilePic: z.string(),
	username: z.string().optional(),
});

export const CompanyUserSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	email: z.string(),
	profilePic: z.string(),
	username: z.string(),
	managerId: z.string().nullable(),
	manager: EmbeddedUserSchema.nullable().optional(),
	directReports: z.array(EmbeddedUserSchema).optional(),
	directReportCount: z.number().optional(),
	bizyAccess: z.boolean().optional(),
	department: z.string().nullable().optional(),
	role: z.enum(['admin', 'employee']).optional(),
});

export type CompanyUser = z.infer<typeof CompanyUserSchema>;

export const CompanyUserListSchema = z.object({
	records: z.array(CompanyUserSchema),
	totalCount: z.number(),
	page: z.number(),
	perPage: z.number(),
	counts: z.object({
		all: z.number(),
		managers: z.number(),
		withoutManager: z.number(),
		bizyEnabled: z.number().optional(),
		bizyDisabled: z.number().optional(),
	}),
	availableDepartments: z.array(z.string()).optional(),
});

export type CompanyUserList = z.infer<typeof CompanyUserListSchema>;

export const companyUserFixture = (overrides: Partial<CompanyUser> = {}): CompanyUser => ({
	id: fixture.id(),
	displayName: 'Test User',
	email: 'test@example.com',
	profilePic: '',
	username: 'testuser',
	managerId: null,
	...overrides,
});

export const teamRequestFixture = (overrides: Partial<TeamRequest> = {}): TeamRequest => ({
	id: fixture.id(),
	companyId: fixture.id(),
	requesterId: fixture.id(),
	state: 'pending',
	submittedAt: new Date().toISOString(),
	decidedAt: null,
	decidedById: null,
	adminNotes: null,
	requester: null,
	decidedBy: null,
	changes: [],
	...overrides,
});
